import { axios } from '../../clients/axios'
import { API_URL } from '../../config/env'
import { formatCurrency } from '../../util/currency'
import { YYYYMMDD, dayjs } from '../../util/date'
import { getTenantName } from '../../util/user'
import {
  parsePaymentStatus,
  parseRefundStatus
} from '../../config/securityDeposit'
import { getUsableAmount } from '../../util/securityDeposit'

const headers = [
  { value: 'refNo', text: 'ID' },
  { value: 'paymentRefNo', text: 'Payment' },
  { value: 'unitName', text: 'Unit' },
  { value: 'tenantName', text: 'Tenant', width: '20%' },
  { value: 'amount', text: 'Amount' },
  { value: 'paymentStatus', text: 'Payment Status' },
  { value: 'refundStatus', text: 'Refund Status' },
  { value: 'refundDate', text: 'Refund Date' },
  { value: 'refundedAmount', text: 'Refunded Amt' },
  // {value: 'attachments', text: 'Files'},
  { value: 'actions', text: 'Actions' }
  // {value: 'data-table-expand'}
]

export default {
  namespaced: true,
  state: {
    securityDeposits: [],
    totalCount: 0
  },
  getters: {
    getHeaders: () => headers,
    getCount: (state) => state.totalCount,
    getSecurityDeposits: (state, getters, rootState, rootGetters) => {
      const aliases = rootGetters['users/getAliases']
      const units = rootGetters['units/getUnitsFlat']
      return state.securityDeposits.map((secDep) => {
        secDep.paymentRefNo =
          secDep.payment_details && secDep.payment_details.refNo
        secDep.dateCreated = dayjs(secDep.dateCreated).format(YYYYMMDD)
        secDep.refundDate = secDep.refundDate
          ? dayjs(secDep.refundDate).format(YYYYMMDD)
          : ''
        secDep.paymentDueDate = secDep.paymentDueDate
          ? dayjs(secDep.paymentDueDate).format(YYYYMMDD)
          : ''
        secDep.tenantName = getTenantName(
          aliases,
          secDep.tenantId,
          secDep.tenant_details
        )
        secDep.formattedAmount = formatCurrency(secDep.amount)
        secDep.formattedRefundedAmount = formatCurrency(secDep.refundedAmount)
        secDep.parsedPaymentStatus = parsePaymentStatus(secDep.paymentStatus)
        secDep.parsedRefundStatus = parseRefundStatus(secDep.refundStatus)
        const unit = units.find(
          (u) => u.bedId === parseInt(secDep.lease_details.bedId)
        )
        secDep.unitName = unit && unit.bedName

        return secDep
      })
    }
  },
  mutations: {
    fetchSecurityDeposits(state, securityDeposits) {
      state.securityDeposits = securityDeposits
    },
    fetchCount(state, count) {
      state.totalCount = count
    }
  },
  actions: {
    async fetchSecurityDepositAction(context, params) {
      const { leaseId, tenantId } = params

      const response = await axios.get(`${API_URL}/securityDeposits`, {
        withCredentials: true,
        params: { leaseId, tenantId }
      })

      const securityDeposits = response.data
      if (!securityDeposits) {
        return {
          securityDeposits: null,
          usableAmount: null
        }
      }

      // get the aggregate data
      const usableAmount = securityDeposits.reduce(
        (amount, securityDeposit) => {
          const usable = getUsableAmount(securityDeposit)
          securityDeposit.usable = usable

          return amount + usable
        },
        0
      )

      return {
        securityDeposits,
        usableAmount
      }
    },
    async searchSecurityDepositsAction(context, params) {
      const paramsToSend = params
      // remove empty string / falsy attributes
      Object.keys(paramsToSend).forEach(
        (k) => !paramsToSend[k] && delete paramsToSend[k]
      )
      paramsToSend.page = (params.page || 1) - 1

      const response = await axios.get(`${API_URL}/securityDeposits/search`, {
        params: paramsToSend,
        withCredentials: true
      })

      const result = response.data
      context.commit('fetchSecurityDeposits', result.securityDeposits)
      context.commit('fetchCount', result.count)

      return {
        count: result.count,
        limit: params.limit,
        securityDeposits: result.securityDeposits
      }
    },
    async receivePayment(context, params) {
      const { securityDepositId, description, datePaid } = params

      const response = await axios.post(
        `${API_URL}/securityDeposits/receivePayment`,
        {
          securityDepositId,
          description,
          datePaid
        },
        {
          withCredentials: true
        }
      )

      return response.data
    },
    async update(context, params) {
      const {
        securityDepositId,
        amount,
        description,
        refundDate,
        paymentDueDate
      } = params

      const response = await axios.put(
        `${API_URL}/securityDeposits/${securityDepositId}`,
        {
          amount,
          description,
          refundDate,
          paymentDueDate
        },
        {
          withCredentials: true
        }
      )

      return response.data
    },
    async create(context, params) {
      const {
        leaseId,
        amount,
        description,
        refundDate,
        paymentDueDate,
        tenantId
      } = params

      const response = await axios.post(
        `${API_URL}/securityDeposits`,
        {
          leaseId,
          amount,
          description,
          refundDate,
          paymentDueDate,
          tenantId
        },
        {
          withCredentials: true
        }
      )

      return response.data
    },

    async approveRefund(context, params) {
      const { securityDepositId } = params

      const response = await axios.post(
        `${API_URL}/securityDeposits/approveRefund`,
        {
          securityDepositId
        },
        {
          withCredentials: true
        }
      )

      return response.data
    },

    async rejectRefund(context, params) {
      const { securityDepositId, reason } = params

      const response = await axios.post(
        `${API_URL}/securityDeposits/rejectRefund`,
        {
          securityDepositId,
          reason
        },
        {
          withCredentials: true
        }
      )

      return response.data
    },

    async refund(context, params) {
      const { securityDepositId, amount } = params

      const response = await axios.post(
        `${API_URL}/securityDeposits/refund`,
        {
          securityDepositId,
          amount
        },
        {
          withCredentials: true
        }
      )

      return response.data
    },
    async cancel(context, params) {
      const { securityDepositId } = params

      const response = await axios.post(
        `${API_URL}/securityDeposits/cancel`,
        {
          securityDepositId
        },
        {
          withCredentials: true
        }
      )

      return response.data
    }
  }
}
